import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { useHistory } from 'react-router-dom';
import { isNewTestament } from '@deaf-bible-society-2-0/deafbible-metadata-utils';

import { formattedBooks, lookupTable } from '../../../../store/translation/selectors';
import { RootState } from '../../../../store';

import fade from '../../../../styles/transitions/fade.module.css';
import { setExpandedItem } from '../../../../store/interface';
import { resolveChapterIntroVerse } from '../../../../utils/introResolver';
import { Title } from '../Title';
import { Content } from '../Content';
import { Chapter } from '../Content/Chapter';
import { ChapterSegments } from '../Content/ChapterSegments';

interface Props {
  filterText: string;
  closeSidebar: () => void;
}

const Books = ({ filterText, closeSidebar }: Props): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { selectedTestament } = useSelector((state: RootState) => state.interface);
  const { darkMode } = useSelector((state: RootState) => state.settings);
  const { code, chapter, bookOrSlug } = useSelector((state: RootState) => state.params);

  const { expandedItem: selectedBook } = useSelector((state: RootState) => state.interface);

  const books = useSelector(formattedBooks);
  const lut = useSelector(lookupTable);

  const scrollToItem = useCallback(() => {
    const book = selectedBook;

    if ((selectedTestament === 'new') === isNewTestament(book || '')) {
      const element = document.getElementById(book || '');
      element?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    } else {
      document.getElementById('content-container')?.scrollTo({ top: 0 });
    }
  }, [selectedBook, selectedTestament]);

  useEffect(() => {
    if ((selectedTestament === 'new') === isNewTestament(bookOrSlug || '')) {
      dispatch(setExpandedItem(bookOrSlug));
    }
  }, [selectedTestament, bookOrSlug, dispatch]);

  useEffect(() => {
    if ((selectedTestament === 'new') === isNewTestament(bookOrSlug || '')) {
      scrollToItem();
    }
  }, [bookOrSlug, scrollToItem, selectedTestament]);

  const setSelectedBook = (newBook: string) => {
    dispatch(setExpandedItem(newBook));
  };

  const filteredBooks = books.filter(
    book =>
      (book.name || '').toLowerCase().includes((filterText || '').toLowerCase()) &&
      book.isNewTestament === (selectedTestament === 'new')
  );

  const loadChapter = useCallback(
    (newBook: string, newChapter: string): void => {
      if (lut === undefined) {
        return;
      }

      try {
        const newVerse = lut.firstVerseNumber(newBook, newChapter);

        // If no verse found (book has no videos), don't navigate
        if (newVerse === undefined || newVerse === null) {
          return;
        }

        const navigate = async () => {
          const preferredVerse = await resolveChapterIntroVerse(
            lut,
            code,
            newBook,
            newChapter,
            `${newVerse}`
          );

          history.push(
            `/${code}/${newBook}.${newChapter}.${preferredVerse}${window.location.search}`
          );
          scrollToItem();
          closeSidebar();
        };

        navigate().catch(() => undefined);
      } catch (error) {
        // Silently handle errors when book has no videos
        // The book will still be displayed but clicking chapters won't navigate
      }
    },
    [closeSidebar, code, history, lut, scrollToItem]
  );

  const key = filteredBooks.reduce((acc, cur) => acc + cur.shortcode, '');

  return (
    <SwitchTransition mode="out-in">
      <CSSTransition
        mountOnEnter
        unmountOnExit
        key={key}
        classNames={fade}
        addEndListener={scrollToItem}
        timeout={300}
      >
        <div>
          {filteredBooks.map(({ name, chapters, shortcode }) => {
            const selectBook = () => setSelectedBook(shortcode);
            const isSelected = selectedBook === shortcode;
            const hasChapters = chapters && chapters.length > 0;

            return (
              <div key={shortcode}>
                <Title isSelected={isSelected} onClick={selectBook} text={name} />
                <Content darkMode={darkMode} show={isSelected}>
                  {hasChapters ? (
                    <>
                      {chapters.map(c => {
                        const selectChapter = () => loadChapter(shortcode, c);

                        return (
                          <Chapter
                            isSelected={bookOrSlug === shortcode && chapter === c}
                            darkMode={darkMode}
                            onClick={selectChapter}
                            key={`${shortcode}${c}`}
                            text={c}
                          />
                        );
                      })}
                      {bookOrSlug === shortcode && <ChapterSegments />}
                    </>
                  ) : (
                    <div
                      style={{
                        padding: '10px 20px',
                        color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                        fontSize: '0.9em',
                        fontStyle: 'italic',
                        textAlign: 'center',
                        width: '100%',
                      }}
                    >
                      Coming Soon!
                    </div>
                  )}
                </Content>
              </div>
            );
          })}
        </div>
      </CSSTransition>
    </SwitchTransition>
  );
};

export default Books;
